import React, { useState, useEffect } from 'react';
import { Body, Box, Heading, SimpleGrid, Icon } from '@hover/blueprint';
import { iCheck, iChevronLeft, iChevronRight, iX } from '@hover/icons';
import Slider from 'react-slick';
import { nanoid } from 'nanoid';
import {
  PRICING_PAGE_TABLE_TABLET_MOBILE,
  PRICING_PAGE_TABLE_HEADER,
  PRICING_TABLE_DATA,
} from '../../../../constants';
import {
  IPricingFeature,
  IPricingSection,
} from '../../../../interfaces/IPricingPageTableData';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import HeaderCard from '../HeaderCard';
import { useSyncTableHeaderAndTableData } from '../../../../hooks';
import PricingTooltip from '../../plans-and-pricing/PricingTooltip';

const HEADERS = PRICING_PAGE_TABLE_HEADER.slice(
  2,
  PRICING_PAGE_TABLE_HEADER.length,
);

const PricingTableTablet: React.FC = () => {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const onWindowSizeChange = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onWindowSizeChange);
    return () => window.removeEventListener('resize', onWindowSizeChange);
  }, []);

  const getTabletMargins = (index: number): string => {
    if (index === 21 && windowWidth < 975 && typeof window !== 'undefined') {
      return '144px';
    }
    return [11, 16, 21].includes(index) ? '108px' : '0';
  };

  const getMobileMargins = (index: number): string => {
    if (typeof window !== 'undefined') {
      if (index === 21 && windowWidth <= 974 && windowWidth > 935) {
        return '108px !important';
      }

      if (index === 16 && windowWidth <= 379) {
        return '80px';
      }

      if (index === 21 && windowWidth <= 422 && windowWidth > 409) {
        return '104px';
      }

      if (index === 11 && windowWidth <= 442 && windowWidth > 435) {
        return '80px';
      }

      if (index === 21 && windowWidth < 425) {
        return '128px';
      }

      if (
        (index === 21 && windowWidth < 634) ||
        (index === 11 && windowWidth < 444) ||
        (index === 16 && windowWidth < 380)
      ) {
        return '104px';
      }
    }
    return [11, 16, 21].includes(index) ? '80px' : '0';
  };

  const getHeight = (index: number): string | number => {
    if (index === 21 && typeof window !== 'undefined') {
      if (windowWidth < 424) {
        return '73px';
      }
      if (windowWidth <= 425) {
        return '73px';
      }
    }
    return [12, 17, 22].includes(index) ? '65px' : 500;
  };

  const { settings, onNext, onPrev, headerRef, tableRef } =
    useSyncTableHeaderAndTableData();

  return (
    <SimpleGrid
      pb="80px"
      columns={2}
      spacing={0}
      d={{ base: 'grid', desktop: 'none' }}
    >
      <Box
        pt={{ base: '211px', tablet: '270px' }}
        pl={{ base: 500, tablet: '56px' }}
        flexDir="column"
        shadow="distance300"
      >
        {PRICING_TABLE_DATA.map((data: IPricingSection, index) => (
          <Box flexDir="column">
            <Box borderBottomWidth="1px" borderColor="neutral.400">
              <Heading
                pr={400}
                mt={{
                  base: index === 0 ? 0 : 700,
                  tablet: index === 0 ? 0 : 800,
                }}
                marginBottom={{ base: 400, tablet: 500 }}
                size={{ base: 200, tablet: 500 }}
                fontWeight={700}
              >
                {data.sectionName}
              </Heading>
            </Box>
            {data.features.map((feature: IPricingFeature) => (
              <Box
                minH={500}
                w="100%"
                borderBottomWidth="1px"
                borderColor="neutral.400"
                alignItems="center"
              >
                <PricingTooltip
                  maxW="300px"
                  pr={400}
                  pl={{ base: 0, tablet: 600 }}
                  tooltip={{
                    id: nanoid(),
                    title: feature.feature,
                    tooltip: feature.tooltip,
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box flexDir="column" position="relative">
        <Box
          position={{ base: 'absolute', tablet: 'static' }}
          pr={{ base: 500, tablet: '56px' }}
          justifyContent="space-between"
          alignItems="center"
          mb={500}
          pl={400}
          top={{ base: '-4px', tablet: 0 }}
          left={0}
          w="100%"
        >
          <Icon
            color="primary.600"
            strokeWidth="2px"
            icon={iChevronLeft}
            size="default"
            m={0}
            zIndex={10}
            onClick={onPrev}
          />
          <Icon
            zIndex={10}
            onClick={onNext}
            strokeWidth="2px"
            color="primary.600"
            icon={iChevronRight}
            size="default"
            m={0}
          />
        </Box>
        <Box
          d="block"
          pl={400}
          pr={{ base: 500, tablet: '56px' }}
          mb={{ base: '59px', tablet: '78px' }}
        >
          <Slider ref={headerRef} {...settings}>
            {HEADERS.map(header => (
              <Box
                key={header.id}
                px={{ base: 0, tablet: '6px' }}
                flexDir="column"
                alingItems="center"
              >
                <Body
                  mb={400}
                  textAlign="center"
                  size={{ base: 300, tablet: 600 }}
                  isBold
                  color="neutral.300"
                >
                  {header.type}
                </Body>
                {/* TODO(links): pricing modal */}
                <HeaderCard card={header} onLinkClick={() => {}} />
              </Box>
            ))}
          </Slider>
        </Box>
        <Box d="block" borderTopWidth="1px" borderColor="neutral.400">
          <Slider ref={tableRef} {...settings}>
            {PRICING_PAGE_TABLE_TABLET_MOBILE.map(column => (
              <Box key={column.id}>
                {column.values.map((value, index) => (
                  <Box
                    key={nanoid()}
                    borderBottomWidth="1px"
                    borderColor="neutral.400"
                    borderTopWidth={[12, 17, 22].includes(index) ? '1px' : 0}
                    h={getHeight(index)}
                    mb={{
                      base: getMobileMargins(index),
                      tablet: getTabletMargins(index),
                    }}
                    alignItems="center"
                  >
                    {typeof value === 'string' ? (
                      <Body textAlign="center" mx="auto" size={400} maxW="80%">
                        {value}
                      </Body>
                    ) : (
                      <Icon
                        icon={value ? iCheck : iX}
                        size="default"
                        mx="auto"
                        color={value ? 'success.600' : 'brandOrange.300'}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Slider>
        </Box>
      </Box>
    </SimpleGrid>
  );
};

export default PricingTableTablet;
